import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import { CalendarOption, OrderOption } from '../../../components/components/CustomSidebar/NeighborsOptions';
import moment from 'moment-timezone';
import './Status.less';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { fetchAllOrdersWithPaginationWrapper } from '../../services/OrderStatus';
import { fetchAllCalendarEvents } from '../../services/Calendar';
import { RequestInstance } from '../../types/OrderStatus';
import { ServiceCalendar, DalilyActivity } from '../../types/Calendar';
import { userFacingDateTimeFormat } from '../../../utils/timezone';
import { ClipboardCheckIcon } from '../icons';

function isServiceCalendar(event: ServiceCalendar | DalilyActivity): event is ServiceCalendar {
    return (event as ServiceCalendar).svcActivityInstance === true;
}

/** Include event if resident has any interaction (attending, waiting list, declined, etc.). */
function hasResidentInteraction(event: ServiceCalendar | DalilyActivity): boolean {
    const attendees = event.residentAttendees;
    return !!(attendees && attendees.length > 0);
}

const attendanceStatusMap = {
    intend_attend: 'Registered',
    attended: 'Attended',
    waiting: 'Waiting list',
    removed: 'Declined',
};

function formatEventAttendanceStatus(status: string): string {
    return attendanceStatusMap[status] || status;
}

type StatusItemType = 'order' | 'event';

interface StatusItem {
    id: string;
    type: StatusItemType;
    title: string;
    timestamp: string;
    date: Date;
    statusLabel: string;
}

interface StatusProps {
    facilityTimezone: string;
    parentLoading: boolean;
}

const Status = ({ facilityTimezone, parentLoading }: StatusProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [items, setItems] = useState<StatusItem[]>([]);

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);

    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]];

    useEffect(() => {
        if (facilityTimezone) {
            fetchTodayStatus();
        }
    }, [facilityTimezone, correspondingSpeak2Resident]);

    const fetchTodayStatus = async () => {
        try {
            if (!correspondingSpeak2Resident || !facilityTimezone) return;

            setLoading(true);

            const nowInFacility = moment().tz(facilityTimezone);
            const todayStart = nowInFacility.clone().startOf('day');
            const todayEnd = nowInFacility.clone().endOf('day');

            const startDateTimeString = todayStart.clone().format('YYYY-MM-DDTHH:mm:ss');
            const endDateTimeString = todayEnd.clone().format('YYYY-MM-DDTHH:mm:ss');

            const facilityId = String(correspondingSpeak2Resident.Facility);
            const residentId = String(correspondingSpeak2Resident._id);

            const [ordersResponse, calendarResponse] = await Promise.all([
                fetchAllOrdersWithPaginationWrapper({
                    facilityId,
                    statusArr: ['Open', 'Accepted', 'Closed'],
                    pageNo: 1,
                    pageSize: 50,
                    startDateTime: startDateTimeString,
                    endDateTime: endDateTimeString,
                    residentId,
                }).catch((error) => {
                    console.error('Error fetching today orders in Status widget:', error);
                    sendToast('error', error instanceof Error ? error.message : 'Failed to fetch today orders');
                    return [];
                }),
                fetchAllCalendarEvents({
                    facilityId,
                    Filter: {
                        startDateTimeString,
                        endDateTimeString,
                    },
                    ResidentIds: [residentId],
                }).catch((error) => {
                    console.error('Error fetching calendar events in Status widget:', error);
                    sendToast('error', error instanceof Error ? error.message : 'Failed to fetch today events');
                    return [];
                }),
            ]);

            const combinedItems: StatusItem[] = [];

            if (ordersResponse && ordersResponse.length > 0) {
                const orders: RequestInstance[] = ordersResponse;
                orders.forEach((order) => {
                    const requestedTime = order.ScheduledTime ? order.ScheduledTime.startDate : order.RequestedTime;
                    const orderDate = moment.tz(requestedTime, facilityTimezone).toDate();
                    const orderTimestamp = moment.tz(requestedTime, facilityTimezone).format(userFacingDateTimeFormat);

                    combinedItems.push({
                        id: order._id,
                        type: 'order',
                        title: order.OrderName || order.ServiceName || 'Service Order',
                        timestamp: orderTimestamp,
                        date: orderDate,
                        statusLabel: order.Status,
                    });
                });
            }

            const allCalendarEvents: (ServiceCalendar | DalilyActivity)[] = Array.isArray(calendarResponse) ? calendarResponse : [];
            const eventsWithInteraction = allCalendarEvents.filter(hasResidentInteraction);

            eventsWithInteraction.forEach((event) => {
                const isService = isServiceCalendar(event);
                const dateStr = isService ? (event as ServiceCalendar).startDate : (event as DalilyActivity).dateTimeString;
                const eventDate = moment.tz(dateStr, facilityTimezone).toDate();
                const eventTimestamp = moment.tz(dateStr, facilityTimezone).format(userFacingDateTimeFormat);
                const title = isService ? (event as ServiceCalendar).name : (event as DalilyActivity).text;
                const status = event.residentAttendees?.[0]?.status || '';
                combinedItems.push({
                    id: event._id,
                    type: 'event',
                    title: title || 'Event',
                    timestamp: eventTimestamp,
                    date: eventDate,
                    statusLabel: formatEventAttendanceStatus(status),
                });
            });

            combinedItems.sort((a, b) => b.date.getTime() - a.date.getTime());
            setItems(combinedItems);
        } catch (error) {
            console.error('Error fetching today status in Status widget:', error);
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch today status');
        } finally {
            setLoading(false);
        }
    };

    if (!correspondingSpeak2Resident) {
        return null;
    }

    // The fetch never runs without a timezone, so a stuck initial `loading`
    // flag must not keep the spinner up. That case shows the empty state.
    const isLoading = parentLoading || (!!facilityTimezone && loading);

    return (
        <div className="status-component home-card-wrap home-card">
            <div className="home-card-header green">
                <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <ClipboardCheckIcon size={18} />
                </span>
                Today&apos;s Status
            </div>
            <div className={`home-card-body home-card-body-scroll${isLoading ? ' is-loading' : ''}`}>
                {isLoading ? (
                    <Loader active inline="centered" />
                ) : items.length === 0 ? (
                    <div className="home-status-empty">
                        <div className="icon" aria-hidden="true">
                            🗓️
                        </div>
                        No orders or events scheduled for today
                    </div>
                ) : (
                    items.map((item) => {
                        const linkTo = item.type === 'order' ? OrderOption.path : CalendarOption.path;
                        return (
                            <Link key={item.id} to={linkTo} className="home-status-item">
                                <div className="home-status-item-header">
                                    <span>{item.type === 'order' ? 'Order' : 'Calendar Event'}</span>
                                    <span>{item.timestamp}</span>
                                </div>
                                <div className="home-status-item-title">{item.title}</div>
                                <div className="home-status-item-status">{item.statusLabel}</div>
                            </Link>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default Status;